import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { EmailSettingsDto, EmployerInqueryDto, JobsDto, TitleAndLocationDto } from 'src/app/data/dtos';
import { AppStateInterface } from 'src/app/store/types';
import { environment } from 'src/environments/environment';
import * as LanguageActions from '../../store/language/actions';
import { selectActiveLanguage } from 'src/app/store/language/selectors';
import * as ColorActions from 'src/app/store/color-theme/actions';
import { selectColorTheme } from 'src/app/store/color-theme/selectors';
import { BrowserService } from '../browser.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { log } from 'console';



@Injectable({
  providedIn: 'root',
})
export class PageService {

  selectedColortheme: string | null = '';

  activeLanguage: string = '';
  languageDirection: string = 'ltr'

  constructor(
    private _store: Store<AppStateInterface>,
    private _httpClient: HttpClient,
    private readonly browserService: BrowserService,
    private _router: Router,
    private _translate: TranslateService,

  ) {
    this._store.dispatch(
      ColorActions.setColorTheme({
        selectedColor: this.browserService.localStorageGetItem('color-theme') ?? 'light',
      }),
    );
    this._store.select(selectColorTheme).subscribe({
      next: result => {
        this.selectedColortheme = result;
      }
    })
  }

  setDirection(language: string) {
    const browserDocument = this.browserService.document;
    if(browserDocument){
      if (language === 'fa') {
        const elements = browserDocument.querySelectorAll('[dir="ltr"]');
        // input elements must alwas be ltr since we dont allow writing text with rtl directions
        const filteredElements = Array.from(elements).filter(el => {
          return el.tagName.toLowerCase() !== 'input' && el.tagName.toLowerCase() !== 'textarea'}
        );
        filteredElements.forEach((element) => {
          element.setAttribute('dir', 'rtl');
        });
      } else {
        const elements = browserDocument.querySelectorAll('[dir="rtl"]');
        // input elements must alwas be ltr since we dont allow writing text with rtl directions
        const filteredElements = Array.from(elements).filter(el => {
          return el.tagName.toLowerCase() !== 'input' && el.tagName.toLowerCase() !== 'textarea' }
        )
        filteredElements.forEach((element) => {
          element.setAttribute('dir', 'ltr');
        });
      }
    }
  }

  setLanguage(newLanguage: string) {
    this._store.dispatch(
      LanguageActions.setActiveLanguage({ activeLanguage: newLanguage }),
    );
    this.browserService.localStorageSetItem('active-language', newLanguage);
    this.setDirection(newLanguage);
    let UrlFragments = this._router.url.split('/');
    UrlFragments[1] = newLanguage;
    this._router.navigate(UrlFragments)
  }

  getActiveLanguage(): string {
    let cachedSelectedLanguage = this.browserService.localStorageGetItem('active-language');
    if (cachedSelectedLanguage && cachedSelectedLanguage != null) {
      return cachedSelectedLanguage;
    }
    this.browserService.localStorageSetItem('active-language', 'en');
    return 'en';
  }

  initLanguage(){
    this._store.select(selectActiveLanguage).subscribe({
      next: (activeLanguage) => {
        this.activeLanguage = activeLanguage;
        this.languageDirection = this.getLanguageDirection(activeLanguage)
        this.setDirection(activeLanguage);

        this._translate.setDefaultLang(activeLanguage);
        this._translate.use(activeLanguage);

        console.log("just set the language and the direction")
      },
    });
  }

  getJobs(query: string): Observable<JobsDto> {
    return this._httpClient.get<JobsDto>(
      `${environment.cms_url}/jobs?${query}`,
    );
  }

  getFAQData(language: string) {
    return this._httpClient.get(
      `${environment.cms_url}/faq-categories?populate=faqs&locale=${language}&sort[0]=id:asc`,
    );
  }

  getLanguageDirection(language: string) {
    if (language === 'fa') {
      return 'rtl';
    } else {
      return 'ltr';
    }
  }
  sendEmployerInquery(employerInquery: EmployerInqueryDto): Observable<HttpResponse<JSON>>{
    return this._httpClient.post<HttpResponse<JSON>>(`${environment.api_url}/employerInquery`, employerInquery,{})
  }

  askFaqQuestion(dto: {question: string}){
    return this._httpClient.post<HttpResponse<JSON>>(`${environment.api_url}/askFaqQuestion`, dto, {})
  }

  subscribe(email: string): Observable<HttpResponse<JSON>>{
    return this._httpClient.post<HttpResponse<JSON>>(`${environment.api_url}/subscribe`, {email: email},{})
  }

  getUserEmailSettings(token: string){
    return this._httpClient.post<EmailSettingsDto>(`${environment.api_url}/getPublicUserEmailSettings`, {token: token})
  }

  saveEmailSettingsViaToken(token: string, dto: EmailSettingsDto){
    return this._httpClient.post<EmailSettingsDto>(`${environment.api_url}/savePublicUserEmailSettingsWithToken`, {token: token, dto: dto})
  }

  saveEmailSettingsViaEmail(email: string, dto: EmailSettingsDto){
    return this._httpClient.post<EmailSettingsDto>(`${environment.api_url}/savePublicUserEmailSettingsWithEmail`, {email: email, dto: dto})
  }

  getTitleAndLocation(query: string){
    return this._httpClient.get<TitleAndLocationDto>(`${environment.cms_url}/jobs${query}`)
  }

  removeOverwrittenBackgroundWhiteTheme(){
    const browserDocument = this.browserService.document;
    if(browserDocument){
      browserDocument.body.classList.remove('bg-gray-200')
    }
  }

  /**
   * Method is used to toggle between color-themes
   * the selected color-theme is fetched in the constructor of this service
   */
  public setTheme(){
    const browserDocument = this.browserService.document;
    if(browserDocument){
      if(this.selectedColortheme === 'light'){
        this._store.dispatch(ColorActions.setColorTheme({ selectedColor: 'dark' }))
        this.browserService.localStorageSetItem('color-theme', 'dark')
        this.removeOverwrittenBackgroundWhiteTheme();
        browserDocument.documentElement.classList.add('dark');
      } else {
        this._store.dispatch(ColorActions.setColorTheme({ selectedColor: 'light' }))
        this.browserService.localStorageSetItem('color-theme', 'light')
        browserDocument.documentElement.classList.remove('dark');
      }
    }
  }

  public includesAny(source: any[], values: any[]): boolean {
    return values.some(value => source.includes(value));
  }
}
